import React, { useState } from 'react';
import axios from 'axios';
import '../CrmStyles.css';
import { LuX, LuBuilding } from 'react-icons/lu';
import { countriesData, getStatesForCountry } from '../../erprecruitment/config/locationConfig';
import BASE_URL from '../../url';

const AddCompanyModal = ({ onClose, onSuccess, companyData = null }) => {
  const [formData, setFormData] = useState({
    companyName: companyData?.CompanyName || '',
    displayName: companyData?.DisplayName || '',
    isIndividual: companyData ? (!companyData.CompanyName && companyData.FirstName) : false,
    firstName: companyData?.FirstName || '',
    lastName: companyData?.LastName || '',
    isClient: companyData?.IsClient || false,
    isSupplier: companyData?.IsSupplier || false,
    isMSP: companyData?.IsMSP !== undefined ? !!companyData.IsMSP : false,
    isActive: companyData?.IsActive !== undefined ? !!companyData.IsActive : true,
    isApproved: companyData?.IsApproved !== undefined ? !!companyData.IsApproved : true,
    industryCode: companyData?.IndustryCode || '',
    email: companyData?.Email || '',
    phone: companyData?.Phone || '',
    website: companyData?.Website || '',
    taxId: companyData?.TaxId || '',
    addressLine1: companyData?.AddressLine1 || '',
    addressLine2: companyData?.AddressLine2 || '',
    city: companyData?.City || '',
    state: companyData?.State || '',
    postalCode: companyData?.PostalCode || '',
    country: companyData?.Country || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [bypassCheck, setBypassCheck] = useState(false);
  const [companyNameError, setCompanyNameError] = useState('');
  const [supplierAccountCreated, setSupplierAccountCreated] = useState(null); // { username, email }

  // ---------------------------------------------------------------------------
  // Country-based Company Name validation
  // ---------------------------------------------------------------------------
  const validateCompanyName = (name, countryCode) => {
    const trimmed = (name || '').trim();
    if (trimmed.length < 2) return 'Company Name must be at least 2 characters.';

    switch (countryCode) {
      case 'IN':
        // Allow Latin letters, Devanagari script, digits, spaces & common punctuation
        if (!/^[\u0900-\u097Fa-zA-Z0-9\s&.,'-]+$/.test(trimmed))
          return 'Company Name (India): only letters (including Devanagari), numbers, spaces, and & . , - \' are allowed.';
        if (/^[0-9]+$/.test(trimmed))
          return 'Company Name (India): cannot be all numbers.';
        break;
      case 'US':
        // English letters, digits, spaces and common legal punctuation only
        if (!/^[a-zA-Z0-9\s&.,'-]+$/.test(trimmed))
          return 'Company Name (US): only English letters, numbers, spaces, and & . , - \' are allowed.';
        break;
      case 'CA':
        // English + French accented characters
        if (!/^[a-zA-Z\u00C0-\u00FF0-9\s&.,'-]+$/.test(trimmed))
          return 'Company Name (Canada): only letters (including French accents), numbers, spaces, and & . , - \' are allowed.';
        break;
      default:
        // Generic: min 2 chars, basic alphanumeric + spaces + punctuation
        if (!/^[a-zA-Z0-9\s&.,'-]+$/.test(trimmed))
          return 'Company Name: only letters, numbers, spaces, and & . , - \' are allowed.';
        break;
    }
    return null; // valid
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCompanyNameError('');

    // 1. Roles
    if (!formData.isClient && !formData.isSupplier && !formData.isMSP) {
      setError('At least one role (Client, Supplier, or Managed Service Provider) must be selected.');
      return;
    }

    // 2. Individual: first + last name
    if (formData.isIndividual && (!formData.firstName || !formData.lastName)) {
      setError('First and Last name are required for individuals.');
      return;
    }

    // 3. Company Name — country-based validation
    if (!formData.isIndividual) {
      const nameErr = validateCompanyName(formData.companyName, formData.country);
      if (nameErr) {
        setCompanyNameError(nameErr);
        setError(nameErr);
        return;
      }
    }

    // 4. Display Name (Short) — mandatory
    if (!formData.displayName || !formData.displayName.trim()) {
      setError('Display Name (Short) is required.');
      return;
    }

    // 5. Status Management — at least one must be checked
    if (!formData.isActive && !formData.isApproved) {
      setError('Status Management: at least "Is Active" or "Is Approved" must be selected.');
      return;
    }

    // 6. Country — mandatory
    if (!formData.country) {
      setError('Country is required. Please select a country under Address Details.');
      return;
    }

    // 7. Email required for Suppliers (used as portal login)
    if (formData.isSupplier && !formData.email) {
      setError('Email is required for Supplier companies (it is used to send portal login credentials).');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      let response;
      const payload = { ...formData, bypassDuplicateCheck: bypassCheck || false };

      if (companyData && companyData.CompanyId) {
        response = await axios.put(`${BASE_URL}/api/crm/companies/${companyData.CompanyId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${BASE_URL}/api/crm/companies`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.success) {
        if (response.data.supplierUsername) {
          setSupplierAccountCreated({
            username: response.data.supplierUsername,
            email: formData.email
          });
          // Show success for a moment then close
          setTimeout(() => onSuccess(), 3000);
        } else {
          onSuccess();
        }
      } else if (response.data.hasSimilar) {
        setSimilarCompanies(response.data.similarCompanies || []);
        setError('Similar company records found. Review the list below.');
      } else {
        setError(response.data.message || 'Failed to save company');
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError(err.response.data.message);
      } else {
        setError(err.response?.data?.message || 'Error saving company');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBypassSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BASE_URL}/api/crm/companies`, {
        ...formData,
        bypassDuplicateCheck: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.message || 'Failed to save company');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="crm-modal-overlay" style={{ zIndex: 100000 }}>
      <div className="crm-modal-content">
        <div className="crm-modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LuBuilding size={20} color="#3b5bfd" /> {companyData && companyData.CompanyId ? 'Edit Company' : 'Add New Company'}
          </h3>
          <button className="crm-modal-close" onClick={onClose}><LuX size={20} /></button>
        </div>
        
        <div className="crm-modal-body">
          {error && <div style={{ color: '#ef4444', marginBottom: '20px', fontSize: '14px', background: '#fef2f2', padding: '10px', borderRadius: '6px', border: '1px solid #fecaca' }}>{error}</div>}

          {/* Supplier account created success banner */}
          {supplierAccountCreated && (
            <div style={{ background: '#dbeafe', border: '1px solid #bfdbfe', color: '#065f46', padding: '14px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
              <strong>✅ Supplier portal account created!</strong><br />
              Credentials have been emailed to <strong>{supplierAccountCreated.email}</strong><br />
              <span style={{ color: '#6b7280', fontSize: '12px' }}>Username: <strong>{supplierAccountCreated.username}</strong> — closing in 3 seconds…</span>
            </div>
          )}
          
          {similarCompanies.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Warning: Possible Duplicate Companies</h5>
              <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontSize: '13px' }}>
                {similarCompanies.map(c => (
                  <li key={c.CompanyId}>
                    <strong>{c.CompanyName}</strong> ({c.CompanyCode}) {c.Email && ` - ${c.Email}`} {c.TaxId && ` (Tax ID: ${c.TaxId})`}
                  </li>
                ))}
              </ul>
              <button type="button" onClick={handleBypassSubmit} className="crm-btn-primary" style={{ background: '#d97706', borderColor: '#d97706', padding: '6px 12px', fontSize: '13px' }} disabled={loading}>
                Bypass & Create Anyway
              </button>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="crm-form-group">
              <label className="crm-checkbox-label" style={{ marginBottom: '16px', background: '#f8f9fa', padding: '12px', borderRadius: '6px', border: '1px solid #eaeaea' }}>
                <input type="checkbox" name="isIndividual" checked={formData.isIndividual} onChange={handleChange} style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }} />
                Is Individual (Sole Proprietor)
              </label>
            </div>

            {formData.isIndividual ? (
              <div className="crm-grid-2">
                <div className="crm-form-group">
                  <label>First Name *</label>
                  <input type="text" name="firstName" className="crm-input" placeholder="e.g. John" value={formData.firstName} onChange={handleChange} required />
                </div>
                <div className="crm-form-group">
                  <label>Last Name *</label>
                  <input type="text" name="lastName" className="crm-input" placeholder="e.g. Doe" value={formData.lastName} onChange={handleChange} required />
                </div>
              </div>
            ) : (
              <div className="crm-grid-2">
                <div className="crm-form-group">
                  <label>Company Name (Legal) *</label>
                  <input
                    type="text"
                    name="companyName"
                    className={`crm-input${companyNameError ? ' crm-input-error' : ''}`}
                    placeholder="e.g. Acme Corporation Inc."
                    value={formData.companyName}
                    onChange={(e) => { handleChange(e); setCompanyNameError(''); }}
                    onBlur={() => {
                      if (!formData.isIndividual) {
                        const err = validateCompanyName(formData.companyName, formData.country);
                        setCompanyNameError(err || '');
                      }
                    }}
                    required
                  />
                  {companyNameError && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {companyNameError}
                    </span>
                  )}
                </div>
                <div className="crm-form-group">
                  <label>Display Name (Short) *</label>
                  <input type="text" name="displayName" className="crm-input" placeholder="e.g. Acme" value={formData.displayName} onChange={handleChange} required />
                </div>
              </div>
            )}

            <div className="crm-grid-2">
              <div className="crm-form-group">
                <label>Roles * (Select at least one)</label>
                <div className="crm-checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  <label className="crm-checkbox-label">
                    <input type="checkbox" name="isClient" checked={formData.isClient} onChange={handleChange} style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }} /> 
                    Client
                  </label>
                  <label className="crm-checkbox-label">
                    <input type="checkbox" name="isSupplier" checked={formData.isSupplier} onChange={handleChange} style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }} /> 
                    Supplier
                  </label>
                  <label className="crm-checkbox-label">
                    <input type="checkbox" name="isMSP" checked={formData.isMSP} onChange={handleChange} style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }} /> 
                    MSP
                  </label>
                </div>
              </div>

              <div className="crm-form-group">
                <label>Status Management * <span style={{ fontWeight: 400, fontSize: '12px', color: '#6b7280' }}>(select at least one)</span></label>
                <div className="crm-checkbox-group">
                  <label className="crm-checkbox-label">
                    <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }} /> 
                    Is Active
                  </label>
                  <label className="crm-checkbox-label">
                    <input type="checkbox" name="isApproved" checked={formData.isApproved} onChange={handleChange} style={{ width: '16px', height: '16px', accentColor: '#3b5bfd' }} /> 
                    Is Approved
                  </label>
                </div>
              </div>
            </div>

            {/* Supplier Portal Access info section */}
            {formData.isSupplier && !companyData?.CompanyId && (
              <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginTop: '4px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '16px' }}>🔐</span>
                  <span style={{ fontWeight: '600', color: '#065f46', fontSize: '14px' }}>Supplier Portal Access</span>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#1d4ed8' }}>
                  A portal login account will be <strong>automatically created</strong> for this Supplier.
                  Login credentials (username &amp; password) will be emailed to the address you enter below.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', color: '#6b7280', background: '#fff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                  <span>📧</span>
                  <span>
                    {formData.email
                      ? <>Will be sent to: <strong>{formData.email}</strong></>
                      : <span style={{ color: '#ef4444' }}>Please fill in the Email field below ↓</span>
                    }
                  </span>
                </div>
              </div>
            )}

            <div className="crm-grid-2">
              <div className="crm-form-group">
                <label>Email</label>
                <input type="email" name="email" className="crm-input" placeholder="contact@example.com" value={formData.email} onChange={handleChange} />
              </div>
              <div className="crm-form-group">
                <label>Phone</label>
                <input type="text" name="phone" className="crm-input" placeholder="(555) 123-4567" value={formData.phone} onChange={handleChange} />
              </div>
            </div>

            <div className="crm-grid-2">
              <div className="crm-form-group">
                <label>Website</label>
                <input type="text" name="website" className="crm-input" placeholder="https://www.example.com" value={formData.website} onChange={handleChange} />
              </div>
              <div className="crm-form-group">
                <label>Tax ID (EIN/SSN)</label>
                <input type="text" name="taxId" className="crm-input" placeholder="XX-XXXXXXX" value={formData.taxId} onChange={handleChange} />
              </div>
            </div>
            
            <div className="crm-form-group">
              <label>Industry</label>
              <select name="industryCode" className="crm-select" value={formData.industryCode} onChange={handleChange}>
                <option value="">Select Industry</option>
                <option value="TECHNOLOGY">Technology</option>
                <option value="HEALTHCARE">Healthcare</option>
                <option value="FINANCE">Finance</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <h4 style={{ marginTop: '24px', marginBottom: '16px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>Address Details</h4>
            
            <div className="crm-grid-2">
              <div className="crm-form-group">
                <label>Street Address</label>
                <input type="text" name="addressLine1" className="crm-input" placeholder="123 Main St" value={formData.addressLine1} onChange={handleChange} />
              </div>
              <div className="crm-form-group">
                <label>Apartment, suite, etc.</label>
                <input type="text" name="addressLine2" className="crm-input" placeholder="Suite 100" value={formData.addressLine2} onChange={handleChange} />
              </div>
            </div>

            <div className="crm-grid-2">
              <div className="crm-form-group">
                <label>Country *</label>
                <select 
                  name="country" 
                  className="crm-select" 
                  value={formData.country} 
                  onChange={(e) => setFormData({ ...formData, country: e.target.value, state: '' })}
                >
                  <option value="">Select Country</option>
                  {countriesData.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="crm-form-group">
                <label>State / Province</label>
                <select name="state" className="crm-select" value={formData.state} onChange={handleChange} disabled={!formData.country}>
                  <option value="">Select State</option>
                  {formData.country && getStatesForCountry(formData.country).map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="crm-grid-2">
              <div className="crm-form-group">
                <label>City</label>
                <input type="text" name="city" className="crm-input" placeholder="New York" value={formData.city} onChange={handleChange} />
              </div>
              <div className="crm-form-group">
                <label>Postal Code</label>
                <input type="text" name="postalCode" className="crm-input" placeholder="10001" value={formData.postalCode} onChange={handleChange} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" onClick={onClose} disabled={loading} className="crm-btn-secondary">Cancel</button>
              <button type="submit" disabled={loading} className="crm-btn-primary">
                {loading ? 'Saving...' : (companyData && companyData.CompanyId ? 'Save Changes' : 'Create Company')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCompanyModal;
